<template>
    <div class="y-video" @click="clickHandler()">
        <picture>
            <img class="y-video__media y-video__media--type--img" :src="thumbnailSrc()" v-on="this.thumbnailListeners" :alt="this.alt"/>
        </picture>
        <button aria-label="Play"><span class="button-text">Play</span><PlayIcon/></button>
    </div>
</template>

<script>
import { startsWith, developmentFixImageUrls } from '../helpers'
import PlayIcon from "../assets/svg/play-arrow.svg"

export default {
  name: 'YouTubeVideo',
  components: {
    PlayIcon
  },
  props: {
    src: {
      type: String,
      required: true,
      validator: value =>
        startsWith(value, 'https://www.youtube.com/embed/') ||
        startsWith(value, 'https://www.youtube-nocookie.com/embed/'),
    },
    alt: {
      type: String,
      default: 'Video thumbnail',
    },
    buttonLabel: {
      type: String,
      default: 'Play video',
    },
    aspectRatio: {
      type: String,
      default: '16:9',
      validator: value => {
        const pattern = /^\d+:\d+$/
        return pattern.test(value)
      },
    },
    previewImageSize: {
      type: String,
      default: 'maxresdefault',
      validator: value =>
        [
          'default',
          'mqdefault',
          'sddefault',
          'hqdefault',
          'maxresdefault',
        ].indexOf(value) !== -1,
    },
    thumbnail: {
      type: String
    },
    iframeAttributes: {
      type: Object,
      default: () => ({
        allowfullscreen: true,
        frameborder: 0,
        allow:
          'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture',
      }),
    },
    webp: {
      type: Boolean,
      default: true,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    thumbnailListeners: {
      type: Object,
    },
  },
  data() {
    const self = this
    return {
      activated: self.autoplay,
    }
  },
  mounted() {
  },
  computed: {
    srcAttribute() {
      const hasQuestionMark =
        typeof this.src === 'string' && this.src.indexOf('?') !== -1
      return `${this.src}${hasQuestionMark ? '&' : '?'}autoplay=1`
    },
    styleObj() {
      return {
        paddingBottom: this.getPaddingBottom(),
      }
    }
  },
  methods: {
    id() {
      const regExp = /^https:\/\/www\.youtube(?:-nocookie)?\.com\/embed\/(.+?)(?:\?.*)?$/
      const executionResult = regExp.exec(this.src)
      if (executionResult !== null) {
        return executionResult[1]
      } else {
        this.warn(`Failed to extract video id from ${this.src}`)
        return ''
      }
    },
    thumbnailSrc() {
      
      if (this.thumbnail) {
        //Only in development
        if (process.env.NODE_ENV == 'development') {
          return developmentFixImageUrls(this.thumbnail);
        }
        return this.thumbnail;
      } else {
        let id = this.id();
        return `https://i.ytimg.com/vi/${id}/${this.previewImageSize}.jpg`
      }
    },
    clickHandler() {
      this.$store.commit('activeVideo', this.id())
    },
    isActivated() {
        return this.activated;
    },
    getPaddingBottom() {
      let { aspectRatio } = this
      // Vue does not provide correct typescript support
      // @ts-ignore
      const defaultAspectRatio = this.$options.props.aspectRatio.default
      const warningMessage = `Invalid value ${aspectRatio} supplied to \`aspectRatio\` property, instead fallback value ${defaultAspectRatio} is used `

      if (typeof aspectRatio === 'string') {
        const [a, b] = aspectRatio.split(':').map(Number)

        if (isFinite(a) === true && isFinite(b) === true) { // eslint-disable-line no-empty
        } else {
          aspectRatio = defaultAspectRatio
          this.warn(warningMessage)
        }
      } else {
        aspectRatio = defaultAspectRatio
        this.warn(warningMessage)
      }

      const [a, b] = aspectRatio.split(':').map(Number)
      return this.getPaddingBottomValue(a, b)
    },
    getPaddingBottomValue(a, b) {
      return `${(b / a) * 100}%`
    },
    warn(message) {
      console.warn(`[vue-lazy-youtube-video]: ${message}`)
    },
  }
}
</script>

<style scoped lang="scss">
img, source {
    width: 100%;
}
.y-video {
    position: relative;
}
button {
    position: absolute;
    top: 50%;
    left: 50%;
    border: 3px solid $color-highlight-primary;
    background-color: $color-background-primary;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    transition: all 0.5s ease-in-out;
    transform: translate(-50%, -50%);
    transform-origin: center;
    svg {
      position: absolute;
      top: 50%;
      left: 55%;
      transform: translate(-50%, -50%);
      path {
        fill: $color-highlight-primary;
      }
    }
}
.button-text {
  opacity: 0;
}
.y-video:hover {
  button {
    width: 75px;
    height: 75px;
  }
}

@media (max-width: 480px) {
  button {
    left: unset;
    right: -30px;
    width: 50px;
    height: 50px;
  }
}

</style>

